/** Typed purchasing and stock-commitment mutations. */
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { usePagedQuery, type Paged } from "@/shared/lib/paging";

/** Traceable request including approval, supplier order and receipts. */
export type PurchaseRequest = Schemas["PurchaseRequestOut"];

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    for (const key of ["procurement", "work-order", "work-orders", "items", "item", "stock"]) {
      void qc.invalidateQueries({ queryKey: [key] });
    }
  };
}

/** Purchasing chains for the active tenant, optionally limited to one work order. */
export function usePurchaseRequests(workOrderId?: string): Paged<PurchaseRequest> {
  return usePagedQuery(["procurement", workOrderId], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/procurement/requests", {
        params: { query: { cursor, work_order_id: workOrderId, limit: 50 } },
      }),
    ),
  );
}

/** Submit an intended material shortage for purchasing approval. */
export function useRequestPurchase(): UseMutationResult<
  PurchaseRequest,
  Error,
  Schemas["PurchaseRequestIn"]
> {
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/procurement/requests", { body })),
    onSuccess: useInvalidate(),
  });
}

/** Approve one submitted purchase request. */
export function useApprovePurchase(): UseMutationResult<PurchaseRequest, Error, string> {
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.POST("/api/v1/procurement/requests/{request_id}/approve", {
          params: { path: { request_id: id } },
        }),
      ),
    onSuccess: useInvalidate(),
  });
}

/** Place an approved purchase with a supplier and delivery commitment. */
export function useOrderPurchase(): UseMutationResult<
  PurchaseRequest,
  Error,
  { id: string; body: Schemas["PurchaseOrderIn"] }
> {
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.POST("/api/v1/procurement/requests/{request_id}/order", {
          params: { path: { request_id: id } },
          body,
        }),
      ),
    onSuccess: useInvalidate(),
  });
}

/** Receive and allocate a partial supplier delivery. */
export function useReceivePurchase(): UseMutationResult<
  PurchaseRequest,
  Error,
  { id: string; body: Schemas["PurchaseReceiptIn"] }
> {
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.POST("/api/v1/procurement/orders/{order_id}/receive", {
          params: { path: { order_id: id } },
          body,
        }),
      ),
    onSuccess: useInvalidate(),
  });
}

/** Cancel the outstanding balance without undoing earlier deliveries. */
export function useCancelPurchase(): UseMutationResult<
  PurchaseRequest,
  Error,
  { id: string; reason: string }
> {
  return useMutation({
    mutationFn: async ({ id, reason }) =>
      unwrap(
        await api.POST("/api/v1/procurement/requests/{request_id}/cancel", {
          params: { path: { request_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: useInvalidate(),
  });
}

/** Reserve or release stock against a specific work-order requirement. */
export function useReservePart(
  woId: string,
): UseMutationResult<
  Schemas["PartOut"],
  Error,
  { partId: string; body: Schemas["PartReservationIn"] }
> {
  return useMutation({
    mutationFn: async ({ partId, body }) =>
      unwrap(
        await api.POST("/api/v1/work-orders/{wo_id}/parts/{part_id}/reserve", {
          params: { path: { wo_id: woId, part_id: partId } },
          body,
        }),
      ),
    onSuccess: useInvalidate(),
  });
}

/** Amend a planned quantity, releasing excess reserved stock atomically. */
export function useChangePartQuantity(
  woId: string,
): UseMutationResult<Schemas["PartOut"], Error, { partId: string; body: Schemas["PartIn"] }> {
  return useMutation({
    mutationFn: async ({ partId, body }) =>
      unwrap(
        await api.PATCH("/api/v1/work-orders/{wo_id}/parts/{part_id}", {
          params: { path: { wo_id: woId, part_id: partId } },
          body,
        }),
      ),
    onSuccess: useInvalidate(),
  });
}
